import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePinSession } from './usePinSession';

/**
 * Five-minute inactivity timer for Tier-2 (PIN) sessions. Any of the listed
 * events resets the timer; on expiry, the PIN session is cleared and the user
 * is bounced back to the lock screen.
 *
 * Architecture §Phase-2B + §4.6.1 — kiosk security mitigation in lieu of
 * device pairing (which is deferred to post-MVP).
 */

const IDLE_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'pointerdown',
  'keydown',
  'visibilitychange',
];

export function useInactivityTimeout(): void {
  const { session, logout } = usePinSession();
  const navigate = useNavigate();
  const timerRef = useRef<number | null>(null);
  // Refs to keep the latest logout/navigate inside the listener closure
  // without forcing it to rebind on every render.
  const logoutRef = useRef(logout);
  const navigateRef = useRef(navigate);
  logoutRef.current = logout;
  navigateRef.current = navigate;

  useEffect(() => {
    if (!session) return;

    function expire() {
      logoutRef.current();
      navigateRef.current('/lock', { replace: true });
    }

    function reset() {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(expire, IDLE_MS);
    }

    reset();
    for (const evt of ACTIVITY_EVENTS) document.addEventListener(evt, reset, { passive: true });
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      for (const evt of ACTIVITY_EVENTS) document.removeEventListener(evt, reset);
    };
  }, [session]);
}
